import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { APP_HUB_URL } from "@/config";
import { useAuth } from "@/auth";

type AppHubContextValue = {
  connection: HubConnection | null;
  isConnected: boolean;
};

const AppHubContext = createContext<AppHubContextValue | undefined>(undefined);

export function AppHubProvider({ children }: { children: React.ReactNode }) {
  const { accessToken } = useAuth();
  const connectionRef = useRef<HubConnection | null>(null);
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const stopConnection = useCallback(async () => {
    const current = connectionRef.current;
    connectionRef.current = null;
    setConnection(null);
    setIsConnected(false);

    if (!current) return;

    try {
      await current.stop();
    } catch (err) {
      console.error("Failed to stop hub connection", err);
    }
  }, []);

  useEffect(() => {
    if (!accessToken || !APP_HUB_URL) {
      void stopConnection();
      return;
    }

    if (connectionRef.current) {
      return;
    }

    const hubConnection = new HubConnectionBuilder()
      .withUrl(APP_HUB_URL, {
        withCredentials: true
      })
      .build();

    connectionRef.current = hubConnection;
    setConnection(hubConnection);

    

    hubConnection.onclose(() => {
      setIsConnected(false);
    });

    let disposed = false;

    const start = async () => {
      try {
        await hubConnection.start();
        await hubConnection.invoke("OnAdminJoined");
        if (!disposed) {
          setIsConnected(true);
        }
      } catch (err) {
        console.error("Failed to start hub connection", err);
        if (!disposed) {
          setIsConnected(false);
        }
      }
    };

    void start();

    return () => {
      disposed = true;
    };
  }, [accessToken, stopConnection]);

  useEffect(() => {
    return () => {
      void stopConnection();
    };
  }, [stopConnection]);

  const value = useMemo<AppHubContextValue>(() => ({ connection, isConnected }), [connection, isConnected]);

  return <AppHubContext.Provider value={value}>{children}</AppHubContext.Provider>;
}

export function useAppHub() {
  const ctx = useContext(AppHubContext);
  if (!ctx) {
    throw new Error("useAppHub must be used within <AppHubProvider>");
  }

  return ctx;
}
